import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaQuestionCircle, FaChevronDown, FaChevronUp, FaBook, FaGraduationCap, FaUsers, FaGlobe, FaClock, FaDollarSign, FaMobile, FaHeadphones, FaLightbulb, FaArrowRight } from 'react-icons/fa';
import SEO from '../components/SEO';

const FAQ: React.FC = () => {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const faqData = [
    {
      category: "Getting Started",
      icon: FaBook,
      color: "blue",
      questions: [
        {
          question: "Is Persian hard to learn?",
          answer: "Persian is often easier than people expect! While it has a different script and some unique grammar features, it's actually simpler than many European languages. Persian has no gender, relatively simple verb conjugations, and many English loanwords. Most learners can read basic Persian within 2-3 months with consistent practice.",
          keywords: ["persian difficulty", "is persian hard", "persian learning difficulty"]
        },
        {
          question: "What's the difference between Farsi and Persian?",
          answer: "Farsi and Persian refer to the same language! 'Persian' is the English name, while 'Farsi' is the native name used by speakers in Iran. The language has three main varieties: Farsi (Iran), Dari (Afghanistan), and Tajik (Tajikistan). All are mutually intelligible but have regional differences in pronunciation and vocabulary.",
          keywords: ["farsi vs persian", "persian vs farsi", "farsi persian difference"]
        },
        {
          question: "How long does it take to learn Persian?",
          answer: "The time varies by individual goals and study time. For basic conversation (A2 level), expect 6-12 months with 30 minutes daily practice. For intermediate fluency (B2), plan 1-2 years. For advanced proficiency (C1+), 2-3 years. Consistent daily practice is more important than intensive study sessions.",
          keywords: ["how long learn persian", "persian learning time", "persian fluency timeline"]
        },
        {
          question: "Do I need to learn the Persian alphabet?",
          answer: "Yes, learning the Persian alphabet is essential for true proficiency. While you can start with transliterations, the Persian script unlocks authentic materials, cultural understanding, and proper pronunciation. Our alphabet course makes it manageable with interactive lessons and spaced repetition.",
          keywords: ["persian alphabet", "farsi alphabet", "persian script", "learn persian letters"]
        }
      ]
    },
    {
      category: "Learning Methods",
      icon: FaGraduationCap,
      color: "green",
      questions: [
        {
          question: "What's the best way to learn Persian fast?",
          answer: "The fastest approach combines multiple methods: 1) Learn the alphabet first, 2) Focus on high-frequency vocabulary, 3) Practice with native speakers, 4) Use spaced repetition for retention, 5) Immerse yourself in Persian media. Our 'Learn Farsi Fast' course implements these strategies with proven techniques.",
          keywords: ["learn persian fast", "fastest way learn farsi", "persian learning methods"]
        },
        {
          question: "Should I learn Persian grammar or vocabulary first?",
          answer: "Start with basic vocabulary and simple phrases, then gradually introduce grammar concepts. Learning 200-300 essential words first gives you something to work with when studying grammar. Our pathways balance both, teaching grammar through practical examples rather than abstract rules.",
          keywords: ["persian grammar", "farsi vocabulary", "persian learning order"]
        },
        {
          question: "How important is pronunciation in Persian?",
          answer: "Pronunciation is crucial in Persian! The language has sounds that don't exist in English, and mispronunciation can change word meanings. Focus on mastering the unique sounds (like 'kh', 'gh', 'q') early. Our audio lessons and pronunciation guides help you develop accurate pronunciation from the start.",
          keywords: ["persian pronunciation", "farsi pronunciation", "persian sounds"]
        },
        {
          question: "Can I learn Persian without a teacher?",
          answer: "Yes! Many learners successfully master Persian through self-study, especially with quality online resources. However, having occasional conversations with native speakers accelerates learning. Our platform provides structured self-study paths plus opportunities to connect with Persian speakers.",
          keywords: ["learn persian alone", "self study persian", "persian without teacher"]
        }
      ]
    },
    {
      category: "Platform & Features",
      icon: FaUsers,
      color: "purple",
      questions: [
        {
          question: "Is Pirooz free to use?",
          answer: "Yes! Pirooz offers a generous free tier with daily vocabulary, basic lessons, and community access. Premium features include unlimited lessons, advanced courses, personalized learning paths, and one-on-one tutoring sessions. You can start learning immediately without any payment.",
          keywords: ["pirooz free", "free persian lessons", "persian app free"]
        },
        {
          question: "What makes Pirooz different from other Persian learning apps?",
          answer: "Pirooz focuses on cultural immersion and real-world application. We teach Persian through stories, cultural context, and practical conversations rather than isolated vocabulary. Our community features, native speaker content, and personalized learning paths create a more engaging experience.",
          keywords: ["best persian app", "pirooz vs duolingo", "persian learning platform"]
        },
        {
          question: "Do you have mobile apps?",
          answer: "Yes! Pirooz is fully responsive and works perfectly on mobile browsers. We're also developing native iOS and Android apps for an even better mobile experience. The web version includes offline capabilities for downloaded lessons.",
          keywords: ["persian app mobile", "farsi app android", "persian app iphone"]
        },
        {
          question: "Can I practice speaking with native speakers?",
          answer: "Absolutely! Pirooz connects you with certified Persian teachers and native speakers for conversation practice. You can book one-on-one sessions, join group conversations, or participate in our community forum. Practice makes perfect!",
          keywords: ["persian conversation practice", "farsi native speakers", "persian tutoring"]
        }
      ]
    },
    {
      category: "Cultural & Travel",
      icon: FaGlobe,
      color: "orange",
      questions: [
        {
          question: "What Persian phrases should I learn for travel?",
          answer: "Essential travel phrases include greetings (سلام - hello), asking for help (کمک می‌خواهم - I need help), directions (کجا است؟ - where is?), and basic politeness (لطفاً - please, متشکرم - thank you). Our 'Persian for Travel' course covers all essential phrases for Iran, Afghanistan, and Tajikistan.",
          keywords: ["persian travel phrases", "farsi for travel", "iran travel language"]
        },
        {
          question: "How important is understanding Persian culture for language learning?",
          answer: "Culture and language are deeply connected in Persian! Understanding cultural concepts like 'taarof' (polite refusal), family relationships, and social customs helps you use the language appropriately. Our cultural lessons integrate language learning with cultural understanding.",
          keywords: ["persian culture", "farsi culture", "iranian culture language"]
        },
        {
          question: "Can I learn Persian for business purposes?",
          answer: "Yes! Persian is valuable for business in Iran, Afghanistan, and Central Asia. Our business Persian course covers formal language, business etiquette, negotiation phrases, and cultural considerations. Many professionals use Persian for trade, diplomacy, and academic research.",
          keywords: ["persian business", "farsi business", "persian for professionals"]
        },
        {
          question: "What Persian dialects should I learn?",
          answer: "Start with Standard Persian (Farsi from Iran) as it's most widely understood and has the most learning resources. Once comfortable, you can explore Dari (Afghanistan) or Tajik (Tajikistan) varieties. All dialects are mutually intelligible, so learning one helps with others.",
          keywords: ["persian dialects", "farsi dialects", "dari vs farsi"]
        }
      ]
    }
  ];

  const getColorClasses = (color: string) => {
    const colors = {
      blue: "from-blue-500 to-blue-600 bg-blue-50 border-blue-200 text-blue-800",
      green: "from-green-500 to-green-600 bg-green-50 border-green-200 text-green-800",
      purple: "from-purple-500 to-purple-600 bg-purple-50 border-purple-200 text-purple-800",
      orange: "from-orange-500 to-orange-600 bg-orange-50 border-orange-200 text-orange-800"
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  // Generate structured data for FAQ
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.flatMap(category => 
      category.questions.map(q => ({
        "@type": "Question",
        "name": q.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": q.answer
        }
      }))
    )
  };

  return (
    <>
      <SEO 
        title="Persian Learning FAQ: Common Questions About Learning Farsi | پیروز Pirooz"
        description="Get answers to the most common questions about learning Persian (Farsi). From difficulty level to learning methods, pronunciation tips, and cultural insights. Expert answers for Persian language learners."
        keywords={[
          'persian learning faq', 'farsi learning questions', 'learn persian help',
          'persian language faq', 'farsi language questions', 'persian learning tips',
          'is persian hard to learn', 'how long learn persian', 'persian alphabet',
          'farsi vs persian', 'persian pronunciation', 'persian grammar',
          'persian culture', 'persian travel phrases', 'persian business language',
          'pirooz faq', 'persian learning platform', 'farsi learning app'
        ]}
        canonicalUrl="/faq"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50" dir="ltr">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mb-6">
              <FaQuestionCircle className="text-white text-3xl" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Get answers to the most common questions about learning Persian (Farsi). 
              From getting started to advanced topics, we've got you covered.
            </p>
          </div>

          {/* FAQ Categories */}
          <div className="space-y-12">
            {faqData.map((category, categoryIndex) => (
              <div key={categoryIndex} className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className={`bg-gradient-to-r ${getColorClasses(category.color).split(' ')[0]} ${getColorClasses(category.color).split(' ')[1]} p-6`}>
                  <div className="flex items-center">
                    <category.icon className="text-white text-2xl mr-4" />
                    <h2 className="text-2xl font-bold text-white">{category.category}</h2>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="space-y-4">
                    {category.questions.map((item, questionIndex) => {
                      const globalIndex = categoryIndex * 100 + questionIndex;
                      const isOpen = openItems.includes(globalIndex);
                      
                      return (
                        <div key={questionIndex} className="border border-gray-200 rounded-xl overflow-hidden">
                          <button
                            onClick={() => toggleItem(globalIndex)}
                            className="w-full text-left p-6 hover:bg-gray-50 transition-colors duration-200 flex items-center justify-between"
                          >
                            <h3 className="text-lg font-semibold text-gray-800 pr-4">
                              {item.question}
                            </h3>
                            {isOpen ? (
                              <FaChevronUp className="text-gray-500 flex-shrink-0" />
                            ) : (
                              <FaChevronDown className="text-gray-500 flex-shrink-0" />
                            )}
                          </button>
                          
                          {isOpen && (
                            <div className="px-6 pb-6">
                              <div className="border-t border-gray-200 pt-4">
                                <p className="text-gray-700 leading-relaxed mb-4">
                                  {item.answer}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {item.keywords.map((keyword, keywordIndex) => (
                                    <span 
                                      key={keywordIndex}
                                      className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full"
                                    >
                                      {keyword}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Related Resources */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mt-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Related Resources</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Link 
                to="/learn-farsi-fast" 
                className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl p-6 hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105"
              >
                <div className="flex items-center justify-between mb-4">
                  <FaClock className="text-2xl" />
                  <FaArrowRight />
                </div>
                <h3 className="text-xl font-bold mb-2">Learn Farsi Fast</h3>
                <p className="text-green-100">Accelerated learning program</p>
              </Link>
              
              <Link 
                to="/persian-alphabet-guide" 
                className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl p-6 hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:scale-105"
              >
                <div className="flex items-center justify-between mb-4">
                  <FaBook className="text-2xl" />
                  <FaArrowRight />
                </div>
                <h3 className="text-xl font-bold mb-2">Alphabet Guide</h3>
                <p className="text-blue-100">Master Persian letters</p>
              </Link>
              
              <Link 
                to="/farsi-vs-persian" 
                className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl p-6 hover:from-purple-600 hover:to-purple-700 transition-all duration-300 transform hover:scale-105"
              >
                <div className="flex items-center justify-between mb-4">
                  <FaLightbulb className="text-2xl" />
                  <FaArrowRight />
                </div>
                <h3 className="text-xl font-bold mb-2">Farsi vs Persian</h3>
                <p className="text-purple-100">Understand the differences</p>
              </Link>
              
              <Link 
                to="/persian-for-travel" 
                className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl p-6 hover:from-orange-600 hover:to-orange-700 transition-all duration-300 transform hover:scale-105"
              >
                <div className="flex items-center justify-between mb-4">
                  <FaGlobe className="text-2xl" />
                  <FaArrowRight />
                </div>
                <h3 className="text-xl font-bold mb-2">Travel Phrases</h3>
                <p className="text-orange-100">Essential travel Persian</p>
              </Link>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center mt-12">
            <h2 className="text-3xl font-bold mb-4">Still Have Questions?</h2>
            <p className="text-xl mb-6 opacity-90">
              Join our community forum or connect with our Persian learning experts
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/forum" 
                className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
              >
                Ask the Community
              </Link>
              <Link 
                to="/teachers" 
                className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition-colors"
              >
                Find a Teacher
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQ;